import React from 'react';
import { Link } from 'react-router-dom';
import { Plus, Settings2, Trash2, Edit2, Tag, List } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
import { useWorkspace } from '../contexts/WorkspaceContext';
export function ConfigurationsList() {
  const {
    formConfigs,
    forms,
    templates,
    newsletters,
    deleteFormConfig
  } = useWorkspace();
  const getFormName = (id: number) => forms.find(f => f.id === id)?.name || 'Unknown Form';
  const getTemplateName = (id: number) => templates.find(t => t.id === id)?.name || 'Unknown Template';
  const getNewsletterNames = (ids: number[]) => {
    if (!ids || ids.length === 0) return [];
    return ids.map(id => newsletters.find(n => n.id === id)?.name || 'Unknown').filter(Boolean);
  };
  return <div className="space-y-6" data-id="element-772">
      <div className="flex items-center justify-between" data-id="element-773">
        <div data-id="element-774">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-775">
            Form Configurations
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-776">
            Configure how forms process submissions, send opt-in emails, and
            route to mailing lists.
          </p>
        </div>
        <Link to="/configuration/new" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-777">
          <Plus className="h-4 w-4" data-id="element-778" />
          New Configuration
        </Link>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-779">
        {formConfigs.length === 0 ? <div className="p-12 text-center" data-id="element-780">
            <Settings2 className="mx-auto h-12 w-12 text-gray-400" data-id="element-781" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-782">
              No configurations yet
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-783">
              Link a form to an opt-in email template to get started.
            </p>
            <div className="mt-6" data-id="element-784">
              <Link to="/configuration/new" className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500" data-id="element-785">
                <Plus className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" data-id="element-786" />
                Create Configuration
              </Link>
            </div>
          </div> : <table className="min-w-full divide-y divide-gray-200" data-id="element-787">
            <thead className="bg-gray-50" data-id="element-788">
              <tr data-id="element-789">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-790">
                  Signup Form
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-791">
                  Opt-In Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-792">
                  Mailing Lists
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-793">
                  Tags
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-794">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-795">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-796">
              {formConfigs.map(config => {
            const mailingLists = getNewsletterNames(config.targetNewsletterIds || []);
            return <tr key={config.id} className="hover:bg-gray-50" data-id="element-797">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-798">
                      {getFormName(config.formId)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-799">
                      {getTemplateName(config.templateId)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-800">
                      {mailingLists.length > 0 ? <div className="flex flex-wrap gap-1" data-id="element-801">
                          {mailingLists.slice(0, 2).map(name => <span key={name} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700" data-id="element-802">
                              <List className="h-3 w-3" data-id="element-803" />
                              {name}
                            </span>)}
                          {mailingLists.length > 2 && <span className="text-xs text-gray-500" data-id="element-804">
                              +{mailingLists.length - 2} more
                            </span>}
                        </div> : <span className="text-xs text-gray-400" data-id="element-805">No lists</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-806">
                      {config.tags && config.tags.length > 0 ? <div className="flex flex-wrap gap-1" data-id="element-807">
                          {config.tags.slice(0, 2).map(tag => <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700" data-id="element-808">
                              <Tag className="h-3 w-3" data-id="element-809" />
                              {tag}
                            </span>)}
                          {config.tags.length > 2 && <span className="text-xs text-gray-500" data-id="element-810">
                              +{config.tags.length - 2} more
                            </span>}
                        </div> : <span className="text-xs text-gray-400" data-id="element-811">No tags</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-812">
                      <StatusBadge status={config.status} data-id="element-813" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-814">
                      <div className="flex items-center justify-end gap-3" data-id="element-815">
                        <button className="text-blue-600 hover:text-blue-900" data-id="element-816">
                          <Edit2 className="h-4 w-4" data-id="element-817" />
                        </button>
                        <button onClick={() => deleteFormConfig(config.id)} className="text-red-600 hover:text-red-900" data-id="element-818">
                          <Trash2 className="h-4 w-4" data-id="element-819" />
                        </button>
                      </div>
                    </td>
                  </tr>;
          })}
            </tbody>
          </table>}
      </div>
    </div>;
}